import React, { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Box, ButtonBase, Typography } from "@mui/material";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import {
  designColors,
  designFontFamily,
  designWeights,
} from "services/constants/designTokens";
import { GlobalBreadcrumb } from "components/DesignSystem/sharedUiPatterns";
import { useHoistedBreadcrumb } from "pages/PayRoll/Shared/settingsBreadcrumb";
import {
  PAYROLL_SETTINGS_MENU,
  settingsGroupAnchor,
  settingsItemHref,
  usePayrollSettingsCrumbs,
} from "./payrollSettingsMenu";

const SettingCard = ({ item, group, onOpen }) => {
  const Icon = group.icon;
  return (
    <ButtonBase
      onClick={onOpen}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        textAlign: "left",
        gap: 1.5,
        p: 1.5,
        backgroundColor: designColors.white,
        border: `1px solid ${designColors.slate200}`,
        borderRadius: "10px",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          borderColor: designColors.slate300,
          boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
        },
        "&.Mui-focusVisible": {
          outline: `2px solid ${designColors.teal}`,
          outlineOffset: 1,
        },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: group.tint.background,
          color: group.tint.color,
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: designFontFamily,
            fontSize: 14,
            fontWeight: designWeights.semibold,
            color: designColors.slate900,
            lineHeight: 1.3,
          }}
        >
          {item.label}
        </Typography>
        <Typography
          sx={{
            mt: 0.25,
            fontFamily: designFontFamily,
            fontSize: 12.5,
            color: designColors.slate600,
            lineHeight: 1.35,
          }}
        >
          {item.description}
        </Typography>
      </Box>
      <ChevronRightRoundedIcon sx={{ fontSize: 18, color: designColors.slate400 }} />
    </ButtonBase>
  );
};

// Payroll Settings > Overview: every settings page as a card, grouped the
// same way as the menu card beside it. The first page Payroll Settings opens.
export const PayrollSettingsOverview = () => {
  const history = useHistory();
  const { hash } = useLocation();
  const settingsCrumbs = usePayrollSettingsCrumbs();
  const breadcrumb = settingsCrumbs("Overview");
  // A group crumb (e.g. "Statutory") opens Overview at that group.
  useEffect(() => {
    const id = (hash || "").replace(/^#/, "");
    if (!id) return;
    const target = document.getElementById(id);
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash]);
  // Inside Payroll Settings the breadcrumb shows at the top of the screen.
  const breadcrumbHoisted = useHoistedBreadcrumb(breadcrumb);
  return (
    <Box sx={{ p: { xs: 1.5, md: 2 }, minWidth: 0, fontFamily: designFontFamily }}>
      {breadcrumbHoisted ? null : <GlobalBreadcrumb trail={breadcrumb} />}
      <Box sx={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 1.25, mb: 1.5 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: designFontFamily,
            fontSize: 22,
            fontWeight: designWeights.heading,
            color: designColors.slate900,
          }}
        >
          Overview
        </Typography>
        <Typography sx={{ fontFamily: designFontFamily, fontSize: 13.5, color: designColors.slate600 }}>
          All payroll settings in one place
        </Typography>
      </Box>

      {PAYROLL_SETTINGS_MENU.map((group) => (
        <Box
          key={group.key}
          id={settingsGroupAnchor(group.key)}
          component="section"
          aria-label={group.label}
          // Clear of the sticky top bar when scrolled to.
          sx={{ mb: 2.5, scrollMarginTop: 80 }}
        >
          <Typography
            component="h2"
            sx={{
              mb: 1,
              fontFamily: designFontFamily,
              fontSize: 15,
              fontWeight: designWeights.heading,
              color: designColors.slate900,
            }}
          >
            {group.label}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gap: 1.25,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
              },
            }}
          >
            {group.items.map((item) => (
              <SettingCard
                key={item.key}
                item={item}
                group={group}
                onOpen={() => history.push(settingsItemHref(item))}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
